import { useCallback, useMemo, useRef, useState, type ChangeEvent, type DragEvent } from 'react'
import { Modal } from 'antd'
import { CheckCircleOutlined, DownloadOutlined } from '@ant-design/icons'
import { useToast } from '@/widgets/common'
import SystemHeader from '@/widgets/layout/SystemHeader'
import { BackButton, DemoSelect } from '@/widgets/demo/ListPrimitives'
import { useAppNavigate, useBackToParent } from '@/hooks'
import { RECIPIENT_CSV_LABELS } from '@/data/recipientFields'
import { downloadCsvString, toCsvString } from '@/utils/download'
import { isHandledAuthError } from '@/services/client'
import { importBatchesApi } from '@/services/importBatches/api'
import type { ImportBatchResponse, ImportErrorResponse } from '@/services/importBatches/types'

type ImportState = 'idle' | 'mapping' | 'preview' | 'uploading' | 'submitted' | 'done'
type MappingDraft = Record<number, string>

const CSV_MAPPING_LS_KEY = 'pamsRecipientCsvColumnMappingV1'

const RECIPIENT_FIELD_DEFS = RECIPIENT_CSV_LABELS.map((label, index) => ({
  key: `f${index + 1}`,
  label,
}))

const RECIPIENT_FIELD_LABEL_MAP = new Map(
  RECIPIENT_FIELD_DEFS.map((field) => [field.key, field.label] as const),
)

const DECISION_ADDRESS_LABEL = '決定通知書住所'

function previewColumnWidthClass(label: string): string {
  const length = Array.from(label).length
  if (length >= 16) return 'w-[16rem] min-w-[16rem] max-w-[16rem]'
  if (length >= 13) return 'w-[13rem] min-w-[13rem] max-w-[13rem]'
  if (length >= 11) return 'w-[12rem] min-w-[12rem] max-w-[12rem]'
  if (length >= 9) return 'w-[10.5rem] min-w-[10.5rem] max-w-[10.5rem]'
  if (length >= 8) return 'w-[9rem] min-w-[9rem] max-w-[9rem]'
  if (label === DECISION_ADDRESS_LABEL) return 'w-[8rem] min-w-[8rem] max-w-[8rem]'
  return 'max-w-[10rem]'
}

function previewHeaderClass(label: string): string {
  const baseClass = 'px-2 py-2 text-left font-medium text-sm text-gray-700 bg-gray-50'
  return `${baseClass} ${previewColumnWidthClass(label)} whitespace-nowrap`
}

function previewCellClass(label: string): string {
  const baseClass = 'px-2 py-2 text-gray-800 whitespace-nowrap truncate'
  return `${baseClass} ${previewColumnWidthClass(label)}`
}

const HEADER_FIELD_LOOKUP = new Map<string, string>()

RECIPIENT_FIELD_DEFS.forEach((field) => {
  registerHeaderAlias(field.key, field.key)
  registerHeaderAlias(field.label, field.key)
})

const REPLACEMENT_CHAR = '�'

function readFileAsRows(file: File): Promise<string[][]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = (event) => {
      const buffer = event.target?.result
      if (!(buffer instanceof ArrayBuffer)) {
        reject(new Error('Failed to read the selected file.'))
        return
      }

      try {
        const text = decodeCsvText(buffer)
        const rows = text
          .split(/\r?\n/)
          .filter((line) => line.trim().length > 0)
          .map(parseCsvLine)
        resolve(rows)
      } catch (error) {
        reject(error instanceof Error ? error : new Error('CSVファイルの文字コードを判定できません。'))
      }
    }

    reader.onerror = () => reject(new Error('Failed to read the selected file.'))
    reader.readAsArrayBuffer(file)
  })
}

// プレビューと取込(バックエンド)で判定方式を統一した堅牢な文字コード判定。
// 受給者口座情報CSV (RecipientBankInfoCsvImportDialog) と同一ロジック:
// BOM があれば UTF-8、無ければ UTF-8 で厳密デコード → 失敗したら Shift_JIS にフォールバック。
// 旧実装はバイト範囲のヒューリスティック(0x81-0x9F なら SJIS)で SJIS/UTF-8 を取り違え、
// UTF-8 ファイルのプレビュー文字化けや、SJIS ファイルとの判定不一致の原因となっていた。
function decodeCsvText(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  if (hasUtf8Bom(bytes)) {
    try {
      return decodeWithoutReplacement(buffer, 'utf-8')
    } catch {
      throw new Error('CSVファイルの文字コードを判定できません。UTF-8またはShift_JISで保存してください。')
    }
  }

  try {
    return decodeWithoutReplacement(buffer, 'utf-8')
  } catch {
    return decodeWithoutReplacement(buffer, 'shift_jis')
  }
}

function decodeWithoutReplacement(buffer: ArrayBuffer, encoding: 'utf-8' | 'shift_jis'): string {
  try {
    const text = new TextDecoder(encoding, { fatal: true }).decode(buffer)
    if (text.includes(REPLACEMENT_CHAR)) {
      throw new Error('replacement character detected')
    }
    return text
  } catch {
    if (encoding === 'shift_jis') {
      throw new Error('CSVファイルの文字コードを判定できません。UTF-8またはShift_JISで保存してください。')
    }
    throw new Error('UTF-8 decode failed')
  }
}

function hasUtf8Bom(bytes: Uint8Array): boolean {
  return bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf
}

function parseCsvLine(line: string): string[] {
  const fields: string[] = []
  let index = 0

  while (index <= line.length) {
    if (index === line.length) {
      fields.push('')
      break
    }

    if (line[index] === '"') {
      let value = ''
      index += 1

      while (index < line.length) {
        if (line[index] === '"') {
          if (line[index + 1] === '"') {
            value += '"'
            index += 2
            continue
          }
          index += 1
          break
        }

        value += line[index]
        index += 1
      }

      fields.push(value)
      if (line[index] === ',') {
        index += 1
      }
      continue
    }

    const commaIndex = line.indexOf(',', index)
    if (commaIndex === -1) {
      fields.push(line.slice(index))
      break
    }

    fields.push(line.slice(index, commaIndex))
    index = commaIndex + 1
  }

  return fields
}

function normalizeSignaturePart(value: string | undefined): string {
  return (value ?? '').trim().toLowerCase()
}

function buildMappingKey(columnCount: number, signatureRow: string[]): string {
  const normalizedSignature = signatureRow
    .slice(0, columnCount)
    .map((value) => normalizeSignaturePart(value))
    .join('|')

  return `recipient-columns:${columnCount}:${normalizedSignature}`
}

function createDefaultMappingDraft(columnCount: number): MappingDraft {
  return Object.fromEntries(
    Array.from({ length: columnCount }, (_, index) => [
      index,
      index < RECIPIENT_FIELD_DEFS.length ? RECIPIENT_FIELD_DEFS[index].key : '',
    ]),
  ) as MappingDraft
}

function normalizeHeaderToken(value: string | undefined): string {
  return (value ?? '').replace(/^\uFEFF/, '').trim().toLowerCase()
}

function compactHeaderToken(value: string): string {
  return value.replace(/[\s\u3000]+/g, '')
}

function registerHeaderAlias(alias: string, fieldKey: string): void {
  const normalized = normalizeHeaderToken(alias)
  if (!normalized) return
  HEADER_FIELD_LOOKUP.set(normalized, fieldKey)
  HEADER_FIELD_LOOKUP.set(compactHeaderToken(normalized), fieldKey)
}

function resolveHeaderFieldKey(value: string | undefined): string | null {
  const normalized = normalizeHeaderToken(value)
  if (!normalized) return null

  const directMatch = HEADER_FIELD_LOOKUP.get(normalized)
  if (directMatch) return directMatch

  const compactMatch = HEADER_FIELD_LOOKUP.get(compactHeaderToken(normalized))
  if (compactMatch) return compactMatch

  const fieldKeyMatch = normalized.match(/^f0*(\d{1,3})$/)
  if (!fieldKeyMatch) return null

  const fieldNumber = Number(fieldKeyMatch[1])
  if (!Number.isInteger(fieldNumber) || fieldNumber < 1 || fieldNumber > RECIPIENT_FIELD_DEFS.length) {
    return null
  }
  return `f${fieldNumber}`
}

function createHeaderMappingDraft(headerRow: string[], columnCount: number): MappingDraft | null {
  const draft: MappingDraft = {}
  const usedTargets = new Set<string>()
  let nonBlankCount = 0

  for (let index = 0; index < columnCount; index += 1) {
    const value = headerRow[index]
    if (value == null || value.trim() === '') continue
    nonBlankCount += 1

    const target = resolveHeaderFieldKey(value)
    if (!target) continue
    if (usedTargets.has(target)) return null

    usedTargets.add(target)
    draft[index] = target
  }

  const matchedCount = Object.keys(draft).length
  const minimumMatches = Math.min(3, nonBlankCount)
  if (nonBlankCount === 0 || matchedCount < minimumMatches || matchedCount * 2 < nonBlankCount) {
    return null
  }
  return draft
}

function loadSavedMapping(mappingKey: string): MappingDraft | null {
  try {
    const raw = window.localStorage.getItem(CSV_MAPPING_LS_KEY)
    if (!raw) return null
    const allMappings = JSON.parse(raw) as Record<string, MappingDraft>
    return allMappings[mappingKey] ?? null
  } catch {
    return null
  }
}

function saveMapping(mappingKey: string, draft: MappingDraft): void {
  try {
    const raw = window.localStorage.getItem(CSV_MAPPING_LS_KEY)
    const allMappings = raw ? (JSON.parse(raw) as Record<string, MappingDraft>) : {}
    allMappings[mappingKey] = draft
    window.localStorage.setItem(CSV_MAPPING_LS_KEY, JSON.stringify(allMappings))
  } catch {
    /* localStorage is best effort */
  }
}

function validateMappingDraft(draft: MappingDraft, columnCount: number): string | null {
  const usedTargets = new Map<string, number>()
  for (let index = 0; index < columnCount; index += 1) {
    const target = draft[index]
    if (!target) continue
    const duplicatedColumnIndex = usedTargets.get(target)
    if (duplicatedColumnIndex != null) {
      const fieldLabel = RECIPIENT_FIELD_LABEL_MAP.get(target) ?? target
      return `第 ${index + 1} 列と第 ${duplicatedColumnIndex + 1} 列が「${fieldLabel}」に重複してマッピングされています。修正してから続行してください。`
    }
    usedTargets.set(target, index)
  }
  return null
}

function getColumnLabel(columnIndex: number): string {
  return RECIPIENT_CSV_LABELS[columnIndex] ?? `f${columnIndex + 1}`
}

function getMappedColumnLabel(columnIndex: number, mappingDraft: MappingDraft): string {
  const fieldKey = mappingDraft[columnIndex]
  if (!fieldKey) return getColumnLabel(columnIndex)
  return RECIPIENT_FIELD_LABEL_MAP.get(fieldKey) ?? fieldKey
}

const DataImport = () => {
  const goBackToParent = useBackToParent()
  const navigate = useAppNavigate()
  const toast = useToast()
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const forceMappingRef = useRef(false)

  const [importState, setImportState] = useState<ImportState>('idle')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewRows, setPreviewRows] = useState<string[][]>([])
  const [firstPreviewRow, setFirstPreviewRow] = useState<string[]>([])
  const [totalRowCount, setTotalRowCount] = useState(0)
  const [detectedColumnCount, setDetectedColumnCount] = useState(0)
  const [mappingKey, setMappingKey] = useState('')
  const [mappingDraft, setMappingDraft] = useState<MappingDraft>({})
  const [mappingError, setMappingError] = useState<string | null>(null)
  const [uploadResult, setUploadResult] = useState<ImportBatchResponse | null>(null)
  const [importErrors, setImportErrors] = useState<ImportErrorResponse[]>([])
  const [mappingBanner, setMappingBanner] = useState<'saved' | 'first-time' | null>(null)

  const previewColumns = useMemo(
    () =>
      Array.from({ length: detectedColumnCount }, (_, index) => ({ index, label: getMappedColumnLabel(index, mappingDraft) })).filter(
        ({ index }) => !!mappingDraft[index],
      ),
    [detectedColumnCount, mappingDraft],
  )

  const resetToIdle = useCallback(() => {
    setImportState('idle')
    setSelectedFile(null)
    setPreviewRows([])
    setFirstPreviewRow([])
    setTotalRowCount(0)
    setDetectedColumnCount(0)
    setMappingKey('')
    setMappingDraft({})
    setMappingError(null)
    setUploadResult(null)
    setImportErrors([])
    setMappingBanner(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [])

  const beginFileFlow = useCallback(async (file: File) => {
    if (!/\.csv$/i.test(file.name)) {
      Modal.error({ content: 'CSVファイル（.csv）のみ取込できます。' })
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      return
    }
    try {
      const rows = await readFileAsRows(file)
      if (rows.length === 0) {
        Modal.error({ content: '選択されたCSVファイルが空です。' })
        return
      }

      const rawColumnCount = rows.reduce((max, row) => Math.max(max, row.length), 0)
      const actualColumnCount = Math.max(rawColumnCount, RECIPIENT_FIELD_DEFS.length)
      if (actualColumnCount === 0) {
        Modal.error({ content: '選択されたCSVファイルに使用可能な列がありません。' })
        return
      }

      const headerMapping = createHeaderMappingDraft(rows[0] ?? [], actualColumnCount)
      const dataRows = headerMapping ? rows.slice(1) : rows
      const nextMappingKey = buildMappingKey(actualColumnCount, rows[0] ?? [])
      const savedMapping = loadSavedMapping(nextMappingKey)
      const nextMappingDraft = savedMapping ?? headerMapping ?? createDefaultMappingDraft(actualColumnCount)

      setSelectedFile(file)
      setPreviewRows(dataRows.slice(0, 5).map((row) => row.slice(0, actualColumnCount)))
      setFirstPreviewRow((dataRows[0] ?? rows[0] ?? []).slice(0, actualColumnCount))
      setTotalRowCount(dataRows.length)
      setDetectedColumnCount(actualColumnCount)
      setMappingKey(nextMappingKey)
      setMappingDraft(nextMappingDraft)
      setMappingError(null)
      setUploadResult(null)
      setImportErrors([])

      if (forceMappingRef.current) {
        forceMappingRef.current = false
        setImportState('mapping')
      } else if (savedMapping || headerMapping || actualColumnCount === RECIPIENT_FIELD_DEFS.length) {
        setMappingBanner(savedMapping ? 'saved' : null)
        setImportState('preview')
      } else {
        setImportState('mapping')
      }
    } catch (error) {
      Modal.error({ content: error instanceof Error ? error.message : 'ファイルの読み込みに失敗しました。' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toast])

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    await beginFileFlow(file)
  }

  const handleDrop = async (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    const file = event.dataTransfer.files?.[0]
    if (!file) return
    await beginFileFlow(file)
  }

  const openFilePicker = () => {
    fileInputRef.current?.click()
  }

  const openMappingSetup = () => {
    if (selectedFile && detectedColumnCount > 0) {
      setMappingError(null)
      setImportState('mapping')
      return
    }
    forceMappingRef.current = true
    fileInputRef.current?.click()
  }

  const confirmMapping = () => {
    const validationMessage = validateMappingDraft(mappingDraft, detectedColumnCount)
    if (validationMessage) {
      setMappingError(validationMessage)
      return
    }

    saveMapping(mappingKey, mappingDraft)
    setMappingError(null)
    setMappingBanner('first-time')
    setImportState('preview')
  }

  const handleUpload = useCallback(async () => {
    if (!selectedFile) return

    setImportState('uploading')

    try {
      const mappingJson = detectedColumnCount > 0 ? JSON.stringify(mappingDraft) : undefined
      // バックエンドは createPendingBatch まで実行して IMPORTING の batch を即返す。
      // 残りの取込処理は非同期スレッドで進行するため、画面側は「取込履歴で確認してください」状態に遷移する。
      const { data: result } = await importBatchesApi.upload(
        selectedFile,
        mappingKey || undefined,
        mappingJson,
      )
      setUploadResult(result)
      setImportErrors([])
      setImportState('submitted')
    } catch (error) {
      if (isHandledAuthError(error)) {
        setImportState('preview')
        return
      }
      setImportState('preview')
    }
  }, [detectedColumnCount, mappingDraft, mappingKey, selectedFile])

  const downloadErrorCsv = useCallback(() => {
    if (importErrors.length === 0) return

    const headers = [...RECIPIENT_CSV_LABELS, 'エラー内容']
    const rows = importErrors.map((row) => {
      const dataCells: string[] =
        row.rawColumns && row.rawColumns.length > 0
          ? [...row.rawColumns]
          : RECIPIENT_CSV_LABELS.map(() => '')
      while (dataCells.length < RECIPIENT_CSV_LABELS.length) {
        dataCells.push('')
      }
      dataCells.length = RECIPIENT_CSV_LABELS.length
      dataCells.push(row.localizedMessage ?? row.errorDetail)
      return dataCells
    })

    downloadCsvString(
      toCsvString(headers, rows),
      `CSV取込_エラーデータのみ_${new Date().toISOString().slice(0, 10)}.csv`,
    )
  }, [importErrors])

  const step2Label = importState === 'mapping' ? '列マッピング' : 'データ確認'

  return (
    <div className="min-h-screen bg-gray-100">
      <SystemHeader breadcrumbs={[{ label: 'CSVデータ取込' }]} />
      <main className="mx-auto max-w-6xl px-6 py-8">
        <BackButton onClick={goBackToParent} />

        {/* 取込手順ステップ */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-6">
          <h3 className="text-sm font-bold text-blue-800 mb-2">取込手順</h3>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-blue-700">
            <span className={`flex items-center space-x-1 ${importState === 'idle' ? 'font-bold' : ''}`}>
              <span className={`w-6 h-6 ${importState === 'idle' ? 'bg-blue-600 text-white' : 'bg-blue-200 text-blue-600'} rounded-full flex items-center justify-center text-xs`}>1</span>
              <span>ファイル選択</span>
            </span>
            <svg className="w-4 h-4 text-blue-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            <span className={`flex items-center space-x-1 ${importState === 'mapping' || importState === 'preview' ? 'font-bold' : ''}`}>
              <span className={`w-6 h-6 ${importState !== 'idle' && importState !== 'done' && importState !== 'submitted' ? 'bg-blue-600 text-white' : 'bg-blue-200 text-blue-600'} rounded-full flex items-center justify-center text-xs`}>2</span>
              <span>{step2Label}</span>
            </span>
            <svg className="w-4 h-4 text-blue-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            <span className={`flex items-center space-x-1 ${importState === 'done' || importState === 'submitted' ? 'font-bold' : ''}`}>
              <span className={`w-6 h-6 ${importState === 'done' || importState === 'submitted' ? 'bg-blue-600 text-white' : 'bg-blue-200 text-blue-600'} rounded-full flex items-center justify-center text-xs`}>3</span>
              <span>取込受付完了</span>
            </span>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          className="hidden"
          onChange={(event) => void handleFileChange(event)}
        />

        {importState === 'idle' && (
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-xl font-bold text-gray-800 mb-6">ファイルアップロード</h2>
            <div
              role="button"
              tabIndex={0}
              onClick={openFilePicker}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  openFilePicker()
                }
              }}
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => void handleDrop(event)}
              className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center hover:border-blue-400 hover:bg-blue-50 transition-all cursor-pointer group"
            >
              <svg className="w-16 h-16 text-gray-400 group-hover:text-blue-500 mx-auto mb-4 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <p className="text-lg font-medium text-gray-700 group-hover:text-blue-600">クリックまたはドラッグ＆ドロップでファイルを選択</p>
              <p className="text-sm text-gray-500 mt-2">対応フォーマット: CSV（生活保護受給者情報）</p>
              <p className="text-xs text-gray-400 mt-3">（プロトタイプ）列名がシステム標準と異なる委託元CSVをシミュレートします。初回のみ列マッピング画面が表示され、設定はブラウザに保存されます。</p>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation()
                  openMappingSetup()
                }}
                className="text-sm text-blue-700 hover:text-blue-900 underline underline-offset-2 cursor-pointer"
              >
                列マッピングを設定する（データ確認の前に対応付け）
              </button>
            </div>
            <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">⚠ 注意: アップロードするファイルは委託者（自治体）から提供された正規のデータファイルを使用してください。</p>
            </div>
          </div>
        )}

        {importState === 'mapping' && (
          <div className="rounded-2xl bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">列マッピング</h3>
                <p className="mt-1 text-sm text-gray-500">
                  委託元CSVの各列がどの取込項目に相当するかを対応付けてください。
                </p>
              </div>
            </div>

            {mappingError ? (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {mappingError}
              </div>
            ) : null}

            <div className="max-h-[70vh] overflow-auto rounded-xl border border-gray-200">
              <table className="w-full min-w-[48rem] text-sm">
                <thead className="sticky top-0 bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">列</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">データ1行目の値</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">取込先項目</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {Array.from({ length: Math.min(detectedColumnCount, RECIPIENT_FIELD_DEFS.length) }, (_, index) => (
                    <tr key={index}>
                      <td className="px-4 py-3 text-gray-500">{index + 1}</td>
                      <td className="px-4 py-3 text-gray-900">{firstPreviewRow[index] || '—'}</td>
                      <td className="px-4 py-3">
                        <DemoSelect
                          value={mappingDraft[index] ?? ''}
                          onChange={(event) => {
                            setMappingDraft((current) => ({ ...current, [index]: event.target.value }))
                            setMappingError(null)
                          }}
                          className="w-full pl-3 pr-9"
                        >
                          <option value="">— 取り込まない —</option>
                          {RECIPIENT_FIELD_DEFS.map((field) => (
                            <option key={field.key} value={field.key}>
                              {field.label}
                            </option>
                          ))}
                        </DemoSelect>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex flex-wrap justify-end gap-3">
              <button
                type="button"
                onClick={resetToIdle}
                className="cursor-pointer rounded-lg border border-gray-300 px-6 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
              >
                キャンセル
              </button>
              <button
                type="button"
                onClick={confirmMapping}
                className="cursor-pointer rounded-lg bg-blue-600 px-6 py-2 text-sm font-medium text-white shadow-md transition hover:bg-blue-700"
              >
                マッピングを保存してプレビューへ
              </button>
            </div>
          </div>
        )}

        {importState === 'preview' && selectedFile && (
          <div className="rounded-xl bg-white p-6 shadow-lg">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-xl font-bold text-gray-800">データプレビュー</h2>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500">ファイル:</span>
                <span className="text-sm font-medium text-gray-800 bg-gray-100 px-3 py-1 rounded">{selectedFile.name}</span>
              </div>
            </div>
            {mappingBanner === 'saved' ? (
              <div className="mb-4 p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-900">
                保存済みの列マッピングを適用しています。マッピングを変更する場合は、ファイルアップロード画面にて、リンクをクリックしてください。
              </div>
            ) : null}
            {mappingBanner === 'first-time' ? (
              <div className="mb-4 p-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-900">
                列マッピングを保存しました。同じ列構成のCSVを次回以降取り込む際は、この画面をスキップします。
              </div>
            ) : null}
            <div className="mb-4 grid grid-cols-1 sm:grid-cols-1 max-w-xs">
              <div className="bg-blue-50 rounded-lg p-3">
                <p className="text-xs text-blue-600">総レコード数</p>
                <p className="text-2xl font-bold text-blue-800">{totalRowCount.toLocaleString()}</p>
              </div>
            </div>
            <div className="overflow-auto rounded-lg border border-gray-200 max-h-[min(70vh,520px)]">
              <table className="w-full min-w-max text-xs">
                <thead className="sticky top-0 z-10 bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-2 py-2 text-left font-medium text-gray-700 whitespace-nowrap bg-gray-50">No.</th>
                    {previewColumns.map(({ label, index }) => (
                      <th key={`${label}-${index}`} className={previewHeaderClass(label)} title={label}>
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {previewRows.map((row, rowIndex) => (
                    <tr key={`${rowIndex}-${row[0] ?? 'row'}`} className="hover:bg-gray-50">
                      <td className="px-2 py-2 text-gray-800 whitespace-nowrap">{rowIndex + 1}</td>
                      {previewColumns.map(({ label, index }) => (
                        <td key={`${rowIndex}-${index}`} className={previewCellClass(label)} title={row[index] ?? ''}>
                          {row[index] ?? ''}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500 mt-2">* プレビューは先頭5件のみ表示しています（取込先項目はシステム標準ラベル）</p>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={resetToIdle}
                className="cursor-pointer rounded-lg border border-gray-300 px-6 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
              >
                キャンセル
              </button>
              <button
                type="button"
                onClick={() => void handleUpload()}
                className="cursor-pointer rounded-lg bg-blue-600 px-6 py-2 text-sm font-medium text-white shadow-md transition hover:bg-blue-700"
              >
                取込を実行
              </button>
            </div>
          </div>
        )}

        {importState === 'uploading' && (
          <div className="rounded-2xl bg-white p-12 text-center shadow-sm">
            <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-blue-100 border-t-blue-600" />
            <p className="mt-4 text-sm text-gray-600">取込処理中…</p>
          </div>
        )}

        {importState === 'submitted' && (
          <div className="rounded-xl bg-white p-8 shadow-lg">
            <div className="mb-8 text-center">
              <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-blue-100">
                <CheckCircleOutlined className="text-4xl text-blue-600" />
              </div>
              <h2 className="mb-2 text-2xl font-bold text-gray-800">取込リクエストを受け付けました</h2>
              <p className="mx-auto max-w-2xl leading-relaxed text-gray-600">
                順次取込処理を行なっております。
                <span className="mt-2 block text-gray-700">
                  処理状況および結果は<strong className="font-semibold text-blue-700">「データ取込」画面</strong>でご確認ください。
                </span>
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-3">
              <button
                type="button"
                onClick={() => navigate('ocr-read-list')}
                className="cursor-pointer rounded-lg bg-blue-600 px-6 py-2 text-white shadow-md transition hover:bg-blue-700"
              >
                データ取込画面へ
              </button>
              <button
                type="button"
                onClick={resetToIdle}
                className="cursor-pointer rounded-lg border border-gray-300 px-6 py-2 transition hover:bg-gray-50"
              >
                続けて取込
              </button>
            </div>
          </div>
        )}

        {importState === 'done' && uploadResult && (
          <div className="rounded-xl bg-white p-8 shadow-lg">
            <div className="mb-8 text-center">
              <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-blue-100">
                <CheckCircleOutlined className="text-4xl text-blue-600" />
              </div>
              <h2 className="mb-2 text-2xl font-bold text-gray-800">取込処理が完了しました</h2>
              <p className="mx-auto max-w-2xl leading-relaxed text-gray-600">
                ファイル <span className="font-medium text-gray-800">{uploadResult.fileName ?? selectedFile?.name ?? 'CSVファイル'}</span> の処理が終了しました。
                <span className="mt-2 block text-gray-700">
                  <strong className="font-semibold text-green-700">エラーとならなかったデータは登録が完了</strong>しました。
                  <strong className="font-semibold text-red-700">エラーとなったデータは内容を修正したうえで、再度取込</strong>を行ってください。
                </span>
              </p>
            </div>

            <div className="mx-auto mb-8 grid max-w-3xl gap-4 sm:grid-cols-3">
              <div className="rounded-lg border border-gray-100 bg-gray-50 p-4 text-center">
                <p className="text-xs text-gray-500">取込総数</p>
                <p className="text-xl font-bold text-gray-800">{uploadResult.totalCount.toLocaleString()}件</p>
              </div>
              <div className="rounded-lg border border-green-100 bg-green-50 p-4 text-center">
                <p className="text-xs text-green-700">登録完了（エラー以外）</p>
                <p className="text-xl font-bold text-green-700">
                  {(uploadResult.successCount ?? uploadResult.confirmedCount ?? 0).toLocaleString()}件
                </p>
              </div>
              <div className="rounded-lg border border-red-100 bg-red-50 p-4 text-center">
                <p className="text-xs text-red-700">エラー</p>
                <p className="text-xl font-bold text-red-700">{uploadResult.errorCount.toLocaleString()}件</p>
              </div>
            </div>

            {uploadResult.errorCount > 0 && (
              <div className="mb-6 flex flex-wrap justify-center gap-3">
                <button
                  type="button"
                  onClick={() => navigate('import-error-edit', { batchId: uploadResult.id })}
                  className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-amber-700"
                >
                  エラー修正
                </button>
                <button
                  type="button"
                  onClick={downloadErrorCsv}
                  className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-amber-700 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-amber-800"
                >
                  <DownloadOutlined />
                  エラーデータのみCSVダウンロード
                </button>
              </div>
            )}

            {importErrors.length > 0 ? (
              <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50/80 p-5">
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <h3 className="text-lg font-bold text-amber-900">エラーデータ一覧</h3>
                </div>
                <p className="mb-3 text-xs text-amber-900/80">
                  修正後は、同じ形式のファイルで再度アップロードし、取込を実行してください。
                </p>

                <div className="overflow-x-auto rounded-lg border border-amber-200/80 bg-white">
                  <table className="w-full text-sm">
                    <thead className="border-b border-amber-200 bg-amber-100/80">
                      <tr>
                        <th className="whitespace-nowrap px-4 py-3 text-left text-sm font-medium text-amber-950">世帯番号</th>
                        <th className="whitespace-nowrap px-4 py-3 text-left text-sm font-medium text-amber-950">世帯員識別番号</th>
                        <th className="whitespace-nowrap px-4 py-3 text-left text-sm font-medium text-amber-950">行番号</th>
                        <th className="min-w-[200px] px-4 py-3 text-left text-sm font-medium text-amber-950">エラー内容</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-amber-100">
                      {importErrors.map((row) => (
                        <tr key={row.id} className="hover:bg-amber-50/50">
                          <td className="px-4 py-3 font-medium">{row.householdNo || '—'}</td>
                          <td className="px-4 py-3">{row.rawColumns?.[12] || '—'}</td>
                          <td className="whitespace-nowrap px-4 py-3">{row.rowNumber}</td>
                          <td className="px-4 py-3 font-medium text-red-800">{row.localizedMessage ?? row.errorDetail}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}

            <div className="flex flex-wrap justify-center gap-3">
              <button
                type="button"
                onClick={resetToIdle}
                className="cursor-pointer rounded-lg border border-gray-300 px-6 py-2 transition hover:bg-gray-50"
              >
                続けて取込
              </button>
              <button
                type="button"
                onClick={goBackToParent}
                className="cursor-pointer rounded-lg bg-blue-600 px-6 py-2 text-white shadow-md transition hover:bg-blue-700"
              >
                戻る
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  )
}

export default DataImport
